import { api } from '../../lib/api.js';

/// O fechamento contábil (ADR 0064) chega pronto da API: a tela mostra a situação, pergunta e
/// pede; quem decide o que bloqueia é o servidor.

export type MonthStatus = 'CLOSED' | 'READY' | 'BLOCKED' | 'WAITING' | 'RUNNING' | 'FUTURE';

export interface UnitYear {
  id: string;
  name: string;
  closedUntil: string | null;
  months: Array<{ month: string; status: MonthStatus; changed: boolean }>;
}

export interface ClosingOverview {
  year: number;
  units: UnitYear[];
}

export interface AccountPart {
  account: { code: string; name: string } | null;
  cents: number;
}

export interface PeriodAdjustment extends AccountPart {
  id: string;
  batchId: string;
  originMonth: string;
  targetMonth: string;
  reason: string;
  createdAt: string;
  createdByName: string | null;
}

export interface DuplicateQuestion {
  day: string;
  declaredCents: number;
  salesCents: number;
  declared: Array<{ description: string; detail: string; cents: number }>;
  sales: Array<{ description: string; cents: number }>;
  review: {
    decision: 'SAME' | 'DIFFERENT';
    reviewedAt: string;
    reviewedByName: string | null;
  } | null;
  changedSinceReview: boolean;
}

export interface ClosingVersion {
  version: number;
  status: 'CLOSED' | 'REOPENED';
  closedAt: string;
  closedByName: string | null;
  reopenedAt: string | null;
  reopenedByName: string | null;
  reopenedReason: string | null;
}

export interface MonthClosingView {
  month: string;
  status: MonthStatus;
  closedUntil: string | null;
  previousOpen: string | null;
  cashClosed: boolean;
  duplicates: DuplicateQuestion[];
  acknowledgement: {
    noAccountCents: number;
    waitingCents: number;
    pendingCommissionsCents: number;
  };
  /// O que fica em aberto ao fechar; nulo quando o mês não está para fechar (ADR 0064, decisão 18).
  openItems: {
    commissions: { pendingCents: number; professionals: number; apuratedCents: number } | null;
    payables: { count: number; cents: number };
    receivables: { count: number; cents: number };
  } | null;
  /// O resultado guardado, por conta do plano.
  closingParts: AccountPart[];
  summary: { netRevenueCents: number; costsCents: number; resultCents: number };
  history: ClosingVersion[];
  change: AccountPart[];
  adjustmentsFrom: PeriodAdjustment[];
  adjustmentsInto: PeriodAdjustment[];
}

export const closingApi = {
  async overview(year: number, today: string): Promise<ClosingOverview> {
    const response = await api.get<ClosingOverview>('/tenant/accounting/closing', {
      params: { year, today },
    });
    return response.data;
  },
  async month(month: string, today: string): Promise<MonthClosingView> {
    const response = await api.get<MonthClosingView>('/tenant/accounting/closing/month', {
      params: { month, today },
    });
    return response.data;
  },
  async close(input: {
    month: string;
    today: string;
    acknowledged: MonthClosingView['acknowledgement'];
  }): Promise<MonthClosingView> {
    const response = await api.post<MonthClosingView>('/tenant/accounting/closing/close', input);
    return response.data;
  },
  async reopen(input: { month: string; today: string; reason: string }): Promise<MonthClosingView> {
    const response = await api.post<MonthClosingView>('/tenant/accounting/closing/reopen', input);
    return response.data;
  },
  async review(input: {
    day: string;
    decision: 'SAME' | 'DIFFERENT';
    declaredCents: number;
    salesCents: number;
  }): Promise<void> {
    await api.post('/tenant/accounting/closing/reviews', input);
  },
  async undoReview(day: string): Promise<void> {
    await api.delete(`/tenant/accounting/closing/reviews/${day}`);
  },
  async adjust(input: { month: string; reason: string }): Promise<MonthClosingView> {
    const response = await api.post<MonthClosingView>(
      '/tenant/accounting/closing/adjustments',
      input,
    );
    return response.data;
  },
};
